<script setup lang="ts">
import { BaseHeader, type BaseHeaderImage } from '@components/base';
import { computed } from 'vue';
import BeliefCard, { type BeliefCardData } from './BeliefCard.vue';

interface BeliefSectionSlide {
  imageUrl: string;
  imageSrcset?: string;
  imageAlt?: string;
}

const props = withDefaults(
  defineProps<{
    /** Section background (hex or CSS color). Empty = default white strip. */
    sectionBgColor?: string;
    introHeadingHtml?: string;
    /** BaseHeader Super Table slots; same order as {{img1}}/{{image1}} in `introHeadingHtml`. From Twig may be a JSON string. */
    introHeadingObjects?: BaseHeaderImage[] | string;
    introDescription?: string;
    /** Optional images above the intro / cards (Craft “Section swiper images”). */
    sectionSwiperSlides?: BeliefSectionSlide[] | string;
    /** From Twig may arrive as a real array or a JSON string depending on Vue/compiler. */
    cards?: BeliefCardData[] | string;
  }>(),
  {
    sectionBgColor: '',
    introHeadingHtml: '',
    introHeadingObjects: () => [],
    introDescription: '',
    sectionSwiperSlides: () => [],
    cards: () => [],
  },
);

function parseIntroHeadingObjects(raw: BaseHeaderImage[] | string | undefined): BaseHeaderImage[] {
  if (raw === undefined || raw === null) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    let s = raw.trim();
    if (s === '' || s === '[]') return [];
    try {
      const parsed = JSON.parse(s) as unknown;
      return Array.isArray(parsed) ? (parsed as BaseHeaderImage[]) : [];
    } catch {
      if (s.includes('&quot;')) {
        s = s.replace(/&quot;/g, '"');
        try {
          const parsed = JSON.parse(s) as unknown;
          return Array.isArray(parsed) ? (parsed as BaseHeaderImage[]) : [];
        } catch {
          /* ignore */
        }
      }
      return [];
    }
  }
  return [];
}

const introHeadingObjectsList = computed((): BaseHeaderImage[] => parseIntroHeadingObjects(props.introHeadingObjects));

function parseSectionSlides(raw: BeliefSectionSlide[] | string | undefined): BeliefSectionSlide[] {
  if (raw === undefined || raw === null) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string' && raw.trim() !== '') {
    try {
      const parsed = JSON.parse(raw) as unknown;
      return Array.isArray(parsed) ? (parsed as BeliefSectionSlide[]) : [];
    } catch {
      return [];
    }
  }
  return [];
}

const sectionSwiperSlidesList = computed((): BeliefSectionSlide[] => parseSectionSlides(props.sectionSwiperSlides as BeliefSectionSlide[] | string | undefined).filter(
  (s) => (s.imageUrl ?? '').trim().length > 0,
),
);

const cardsList = computed((): BeliefCardData[] => {
  const raw = props.cards as BeliefCardData[] | string | undefined;
  let list: BeliefCardData[] = [];
  if (Array.isArray(raw)) list = raw;
  else if (typeof raw === 'string' && raw.trim() !== '') {
    try {
      const parsed = JSON.parse(raw) as unknown;
      list = Array.isArray(parsed) ? (parsed as BeliefCardData[]) : [];
    } catch {
      list = [];
    }
  }
  return list.filter((card) => {
    const img = card.imageUrl?.trim() ?? '';
    const title = (card.titleHtml ?? '').replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim();
    const body = (card.body ?? '').trim();
    return img.length > 0 || title.length > 0 || body.length > 0;
  });
});

const sectionBgStyle = computed((): Record<string, string> | undefined => {
  const c = props.sectionBgColor?.trim();
  if (!c) return undefined;
  return { backgroundColor: c };
});

const show = computed(() => {
  const rawHeading = props.introHeadingHtml ?? '';
  const headingText = rawHeading.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim();
  const desc = props.introDescription?.trim() ?? '';
  return (
    headingText.length > 0
    || desc.length > 0
    || cardsList.value.length > 0
    || sectionSwiperSlidesList.value.length > 0
  );
});

const introHeadingContentClass = 'text-28 font-semibold leading-125 tracking-tight text-off-black-500 sm:text-30 md:text-32 lg:text-[52px] lg:leading-110 lg:tracking-normal [&_p]:m-0 [&_p]:leading-[inherit]';

/** Desktop carousel: wheel/trackpad scrolls horizontally without a native scrollbar. */
const swiperMousewheel = { enabled: true, forceToAxis: true };
const swiperNavigation = { enabled: true };
const sectionSwiperMousewheel = { enabled: true, forceToAxis: true };
const sectionSwiperNavigation = { enabled: true };
</script>

<template>
  <section
    v-if="show"
    class="beliefs-section min-w-0 max-w-full pb-48 pt-16 font-inter md:pb-80 md:pt-48 lg:pb-100 lg:pt-60"
    :class="{ 'bg-white': !sectionBgStyle }"
    :style="sectionBgStyle"
  >
    <div class="container min-w-0 max-w-full">
      <div
        v-if="sectionSwiperSlidesList.length"
        class="mx-auto mt-4 min-w-0 max-w-7xl px-8 md:mt-12 md:px-16 lg:px-24"
      >
        <swiper-container
          class="beliefs-section-swiper min-w-0 rounded-[20px] shadow-sm"
          slides-per-view="auto"
          :space-between="12"
          :free-mode="true"
          :grab-cursor="true"
          :mousewheel="sectionSwiperMousewheel"
          :navigation="sectionSwiperNavigation"
          :slides-offset-after="24"
        >
          <swiper-slide
            v-for="(slide, index) in sectionSwiperSlidesList"
            :key="`sv-${index}`"
            class="!h-auto !w-[min(85vw,420px)] sm:!w-[min(72vw,520px)] lg:!w-[560px]"
          >
            <div class="relative aspect-[16/9] w-full overflow-hidden rounded-[16px] bg-warm-grey-30">
              <img
                :src="slide.imageUrl"
                :srcset="slide.imageSrcset || undefined"
                :alt="slide.imageAlt || ''"
                class="absolute inset-0 size-full object-cover"
                loading="lazy"
                decoding="async"
              >
            </div>
          </swiper-slide>
        </swiper-container>
      </div>

      <header
        v-if="introHeadingHtml || introDescription?.trim()"
        class="mx-auto max-w-7xl px-16 text-center lg:px-24"
        :class="sectionSwiperSlidesList.length ? 'mt-16 md:mt-36 lg:mt-40' : ''"
      >
        <BaseHeader
          v-if="introHeadingHtml"
          as="div"
          :content="introHeadingHtml"
          :objects="introHeadingObjectsList"
          :content-class="introHeadingContentClass"
        />
        <p
          v-if="introDescription?.trim()"
          class="leading-140 mx-auto mt-20 max-w-[72ch] text-[15.5px] text-off-black-500 md:mt-24 md:text-18"
        >
          {{ introDescription }}
        </p>
      </header>

      <!-- Mobile / tablet: stay in container -->
      <div
        v-if="cardsList.length"
        class="mt-24 flex min-w-0 max-w-full flex-col gap-16 px-8 md:mt-48 md:px-16 lg:hidden"
      >
        <BeliefCard
          v-for="(card, index) in cardsList"
          :key="`m-${index}`"
          class="w-full shrink-0"
          :card="card"
        />
      </div>
    </div>

    <!-- Desktop: full-width strip (outside .container) — matches Figma; room for all cards -->
    <div
      v-if="cardsList.length"
      class="mt-36 hidden w-full min-w-0 max-w-full md:mt-48 lg:mt-56 lg:block"
    >
      <div class="min-w-0 px-20 lg:px-40">
        <swiper-container
          class="beliefs-swiper min-w-0"
          slides-per-view="auto"
          :space-between="12"
          :free-mode="true"
          :grab-cursor="true"
          :mousewheel="swiperMousewheel"
          :navigation="swiperNavigation"
          :slides-offset-after="32"
        >
          <swiper-slide
            v-for="(card, index) in cardsList"
            :key="`d-${index}`"
            class="!h-auto !w-[272px] xl:!w-[292px]"
          >
            <BeliefCard
              class="size-full"
              :card="card"
            />
          </swiper-slide>
        </swiper-container>
      </div>
    </div>
  </section>
</template>

<style scoped>
.beliefs-swiper::part(button-next),
.beliefs-swiper::part(button-prev) {
  width: 12px;
  height: 12px;
  background: white;
  border-radius: 50%;
  box-shadow: 0 2px 8px rgb(0 0 0 / 0.12);
  color: #1a1a1a;
  --swiper-navigation-size: 14px;
  padding: 12px;
}

.beliefs-swiper::part(button-next-disabled),
.beliefs-swiper::part(button-prev-disabled) {
  opacity: 0;
  pointer-events: none;
}

.beliefs-section-swiper::part(button-next),
.beliefs-section-swiper::part(button-prev) {
  width: 12px;
  height: 12px;
  background: white;
  border-radius: 50%;
  box-shadow: 0 2px 8px rgb(0 0 0 / 0.12);
  color: #1a1a1a;
  --swiper-navigation-size: 14px;
  padding: 12px;
}

.beliefs-section-swiper::part(button-next-disabled),
.beliefs-section-swiper::part(button-prev-disabled) {
  opacity: 0;
  pointer-events: none;
}
</style>
